import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Alert, Container } from "@mui/material";
import { privateApi, publicApi } from "../../../api/axiosConfig";

import { useCart } from "../../Cart/CartContext";
import { useAuth } from "../../../services/auth";
import ProductDetailUI from "./ProductDetailUI";
import NotFoundPage from "../../404/NotFoundPage";
import { useInventoryRequestStatus } from "../../../hooks/useInventoryRequestStatus";

export default function ProductDetail() {
  const { updateCartCount } = useCart();
  const { isAuthenticated } = useAuth();
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cart States
  const [cartQuantity, setCartQuantity] = useState(0);
  const [cartLoading, setCartLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Favorite / Wishlist States
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const [requestLoading, setRequestLoading] = useState(false);
  const [requestQuantity, setRequestQuantity] = useState(1);
  const [requestSuccess, setRequestSuccess] = useState("");

  const [requestedVariantIds, setRequestedVariantIds] = useState(
    () => new Set(),
  );

  const requestAlreadySubmitted =
    selectedVariant?.id != null && requestedVariantIds.has(selectedVariant.id);

  // Inventory request status polling
  const {
    notification: inventoryNotification,
    dismissNotification: dismissInventoryNotification,
    status: inventoryStatus,
  } = useInventoryRequestStatus({
    variantId: selectedVariant?.id,
    enabled: isAuthenticated && !!selectedVariant?.id,
  });

  const handleViewInventoryDetails = () => {
    navigate("/inventory-check-requests");
  };

  const translateCartBackendMessage = (raw) => {
    if (!raw) return "خطایی رخ داده است. لطفا دوباره تلاش کنید.";
    const text = String(raw);
    // Backend already localizes some messages (e.g. stock detail) — keep them.
    if (/[؀-ۿ]/.test(text)) return text;
    const lower = text.toLowerCase();
    if (lower.includes("out of stock")) return "موجودی این کالا تمام شده است.";
    if (lower.includes("insufficient available stock")) return "موجودی انبار برای این تعداد کافی نیست.";
    if (
      lower.includes("stock") ||
      lower.includes("inventory") ||
      lower.includes("موجودی")
    )
      return "تعداد درخواستی بیشتر از موجودی انبار است.";
    if (lower.includes("less than or equal to 5") || lower.includes("exceed"))
      return "حداکثر تعداد مجاز برای هر کالا ۵ عدد است.";
    if (lower.includes("greater than or equal to 1"))
      return "تعداد باید حداقل ۱ عدد باشد.";
    if (
      lower.includes("invalid pk") ||
      lower.includes("object does not exist") ||
      lower.includes("invalid variant")
    )
      return "این کالا نامعتبر است.";
    return "خطایی رخ داده است. لطفا دوباره تلاش کنید.";
  };

  const extractErrorMessage = (err) => {
    if (err.response?.status === 401) {
      return "لطفا برای انجام این عملیات ابتدا وارد حساب کاربری خود شوید.";
    }
    if (err.response && err.response.data) {
      const data = err.response.data;
      if (data.detail) return translateCartBackendMessage(data.detail);
      if (data.error) return translateCartBackendMessage(data.error);
      if (data.message) return translateCartBackendMessage(data.message);
      if (data.non_field_errors)
        return translateCartBackendMessage(data.non_field_errors[0]);

      const firstKey = Object.keys(data)[0];
      if (firstKey && Array.isArray(data[firstKey])) {
        return translateCartBackendMessage(data[firstKey][0]);
      }

      if (typeof data === "string") return translateCartBackendMessage(data);
    }
    return "خطایی رخ داده است. لطفا دوباره تلاش کنید.";
  };

  // 1. Fetch Product Details (Public Endpoint)
  useEffect(() => {
    if (!productSlug) {
      setError("شناسه محصول یافت نشد");
      setLoading(false);
      return;
    }

    const fetchProductDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await publicApi.get(`/api/v1/products/info/${productSlug}/`);
        const data = response.data;

        setProduct(data);

        const variants = data.product_variants || [];

        const defaultVariant =
          variants.find(
            (variant) => variant.has_discount && Number(variant.stock || 0) > 0,
          ) ||
          variants.find((variant) => Number(variant.stock || 0) > 0) ||
          variants[0] ||
          null;

        setSelectedVariant(defaultVariant);

        document.title = data.title || "فروشگاه Zomioo";
      } catch (err) {
        if (err.response?.status === 404) {
          setError(404);
        } else {
          setError("مشکلی در دریافت اطلاعات محصول به وجود آمد.");
          document.title = "خطا در دریافت اطلاعات";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productSlug]);

  const handleRequestInventoryCheck = async (requestedQuantity) => {
    if (!selectedVariant?.id) return;

    if (!isAuthenticated) {
      setActionError("لطفا برای ثبت درخواست ابتدا وارد حساب کاربری خود شوید.");
      return;
    }

    const quantity = Number(requestedQuantity);

    if (!Number.isInteger(quantity) || quantity < 1) {
      setActionError("تعداد بسته باید حداقل ۱ باشد.");
      return;
    }

    setRequestLoading(true);
    setActionError(null);
    setRequestSuccess("");

    try {
      const response = await privateApi.post(
        "/api/v1/checkout/inventory-check-requests/",
        {
          variant: selectedVariant.id,
          requested_package_quantity: quantity,
        },
      );

      const data = response.data;

      setRequestedVariantIds((previous) => {
        const next = new Set(previous);
        next.add(selectedVariant.id);
        return next;
      });

      if (data.status === "APPROVED") {
        setRequestSuccess(
          "برای این کالا پیشنهاد تأییدشده وجود دارد. برای تغییر تعداد، فاکتور را بررسی کنید.",
        );
      } else {
        setRequestSuccess(
          data.detail === "Pending inventory request adjusted."
            ? "تعداد بسته در درخواست قبلی به‌روزرسانی شد."
            : data.detail === "An active inventory request already exists."
              ? "درخواست فعال قبلی شما همچنان ثبت است."
              : "درخواست بررسی موجودی و قیمت ثبت شد. منتظر تأیید فروشنده باشید.",
        );
      }

      // Both 200 (existing request) and 201 (new request) are successful.
      if (response.status === 200 || response.status === 201) {
        navigate("/inventory-check-requests");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setActionError(
          "برای این کالا پیشنهاد تأییدشده‌ای با تعداد متفاوت وجود دارد. برای تغییر تعداد، فاکتور را بررسی کنید.",
        );
      } else {
        setActionError(extractErrorMessage(err));
      }
    } finally {
      setRequestLoading(false);
    }
  };

  // 2. Fetch User Favorites Status when authenticated
  useEffect(() => {
    if (product && isAuthenticated) {
      const fetchFavoriteStatus = async () => {
        try {
          const response = await privateApi.get("/api/v1/products/likes/");
          const likesList = response.data.results || response.data || [];

          // Checks if current product exists in user's liked list
          const isLiked = likesList.some((item) => {
            const likedProductId = item.product?.id || item.product || item.id;
            return likedProductId === product.id;
          });

          setIsFavorite(isLiked);
        } catch (err) {
          console.error("Could not fetch user favorites", err);
        }
      };

      fetchFavoriteStatus();
    } else {
      setIsFavorite(false);
    }
  }, [product, isAuthenticated]);

  // 3. Fetch Initial Cart Status when authenticated
  useEffect(() => {
    if (selectedVariant && isAuthenticated) {
      const fetchCartStatus = async () => {
        try {
          const response = await privateApi.get("/api/v1/carts/list/destroy/");
          const cartItems = response.data.results || response.data;

          const existingItem = cartItems.find(
            (item) => item.variant.id === selectedVariant.id,
          );
          setCartQuantity(existingItem ? existingItem.quantity : 0);
        } catch (err) {
          console.error("Could not fetch cart status", err);
        }
      };

      fetchCartStatus();
    } else {
      setCartQuantity(0);
    }
  }, [selectedVariant, isAuthenticated]);

  

  // Toggle Favorite / Wishlist Action
  const handleToggleFavorite = async () => {
    if (!product) return;

    if (!isAuthenticated) {
      setActionError(
        "لطفا برای افزودن به لیست علاقه‌مندی‌ها ابتدا وارد حساب کاربری خود شوید.",
      );
      return;
    }

    setFavoriteLoading(true);
    setActionError(null);
    try {
      await privateApi.post("/api/v1/products/likes/", { product: product.id });
      setIsFavorite((prev) => !prev);
    } catch (err) {
      console.error("Error toggling favorite", err);
      setActionError(extractErrorMessage(err));
    } finally {
      setFavoriteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        dir="rtl"
      >
        <CircularProgress sx={{ color: "#ef4056" }} />
      </Box>
    );
  }

  if (error === 404) {
    return <NotFoundPage />;
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, direction: "rtl" }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!product) return null;

  return (
    <ProductDetailUI
      product={product}
      selectedVariant={selectedVariant}
      onSelectVariant={setSelectedVariant}
      cartQuantity={cartQuantity}
      handleRequestInventoryCheck={handleRequestInventoryCheck}
      requestQuantity={requestQuantity}
      setRequestQuantity={setRequestQuantity}
      requestLoading={requestLoading}
      isFavorite={isFavorite}
      favoriteLoading={favoriteLoading}
      handleToggleFavorite={handleToggleFavorite}
      actionError={actionError}
      setActionError={setActionError}
      requestSuccess={requestSuccess}
      setRequestSuccess={setRequestSuccess}
      requestAlreadySubmitted={requestAlreadySubmitted}
      inventoryNotification={inventoryNotification}
      onDismissInventoryNotification={dismissInventoryNotification}
      onViewInventoryDetails={handleViewInventoryDetails}
      inventoryStatus={inventoryStatus}
    />
  );
}
